'use client';
import React, { useState } from 'react';

interface Role {
  id: string;
  title: string;
  description: string;
  commitment: string;
  spots: number;
  icon: React.ReactNode;
  accent: string;
}

const roles: Role[] = [
  {
    id: 'patron',
    title: 'Heritage Patron',
    description:
      'Champion the preservation of classical arts and cultural heritage. Your patronage directly funds performances, artist residencies, and archival projects across Madhya Pradesh.',
    commitment: 'Annual commitment',
    spots: 12,
    accent: '#C9A84C',
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
        <path d="M16 3L19.5 11.5H29L21.5 17L24.5 26L16 20.5L7.5 26L10.5 17L3 11.5H12.5L16 3Z" stroke="#C9A84C" strokeWidth="1.5" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: 'curator',
    title: 'Event Curator',
    description:
      'Shape the cultural calendar by discovering and championing emerging artists, classical ensembles, and heritage performances worthy of the grand stage.',
    commitment: '8 hrs / month',
    spots: 25,
    accent: '#8B1A1A',
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
        <rect x="4" y="6" width="24" height="20" rx="2" stroke="#C9A84C" strokeWidth="1.5" />
        <path d="M4 12H28" stroke="#C9A84C" strokeWidth="1.5" />
        <path d="M10 6V12" stroke="#C9A84C" strokeWidth="1.5" />
        <path d="M22 6V12" stroke="#C9A84C" strokeWidth="1.5" />
        <path d="M9 18H14" stroke="#C9A84C" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M9 22H18" stroke="#C9A84C" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'ambassador',
    title: 'Cultural Ambassador',
    description:
      'Represent EventSphere at heritage festivals, royal courts, and cultural gatherings. Be the living bridge between tradition and the modern audience.',
    commitment: '4 hrs / month',
    spots: 50,
    accent: '#1A2A4A',
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
        <circle cx="16" cy="11" r="5" stroke="#C9A84C" strokeWidth="1.5" />
        <path d="M5 28C5 22.48 9.93 18 16 18C22.07 18 27 22.48 27 28" stroke="#C9A84C" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M21 14L26 19" stroke="#C9A84C" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="27" cy="20" r="2" stroke="#C9A84C" strokeWidth="1.5" />
      </svg>
    ),
  },
  {
    id: 'volunteer',
    title: 'Heritage Volunteer',
    description:
      'Bring events to life behind the scenes — from stage management and guest coordination to archival documentation of each performance.',
    commitment: 'Per event',
    spots: 200,
    accent: '#1A3A2A',
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
        <path d="M16 6C16 6 8 10 8 18C8 22.42 11.58 26 16 26C20.42 26 24 22.42 24 18C24 10 16 6 16 6Z" stroke="#C9A84C" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M12 18L15 21L20 15" stroke="#C9A84C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

export default function JoinUsSection() {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  return (
    <section className="py-20 relative overflow-hidden" style={{ background: '#1A0A00' }}>
      {/* Ornamental background pattern */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `radial-gradient(circle at 20% 50%, #C9A84C 1px, transparent 1px), radial-gradient(circle at 80% 20%, #C9A84C 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }}
      />
      {/* Gold top border */}
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, #C9A84C, transparent)' }} />

      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase mb-3" style={{ color: '#C9A84C', fontFamily: 'Roboto, sans-serif' }}>
              Become Part of the Legacy
            </p>
            <h2
              className="text-4xl md:text-5xl leading-tight"
              style={{ fontFamily: '"Playfair Display", Georgia, serif', color: '#FAF7F2' }}
            >
              Join Us
            </h2>
            <div className="mt-4 w-16 h-px" style={{ background: 'linear-gradient(90deg, #C9A84C, transparent)' }} />
          </div>
          <p className="max-w-md text-sm leading-relaxed" style={{ color: '#A89070', fontFamily: 'Roboto, sans-serif' }}>
            EventSphere is more than a platform — it is a living institution. We invite patrons, curators, ambassadors, and volunteers to shape the future of India's cultural heritage.
          </p>
        </div>

        {/* Asymmetric bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {/* Large featured card — Heritage Patron */}
          <div
            className="md:col-span-5 md:row-span-2 relative p-8 flex flex-col justify-between cursor-pointer transition-all duration-300"
            style={{
              background: hoveredId === 'patron' ? 'rgba(201,168,76,0.12)' : 'rgba(201,168,76,0.06)',
              border: `1px solid ${hoveredId === 'patron' ? 'rgba(201,168,76,0.5)' : 'rgba(201,168,76,0.2)'}`,
              borderRadius: '16px',
              minHeight: '340px',
            }}
            onMouseEnter={() => setHoveredId('patron')}
            onMouseLeave={() => setHoveredId(null)}
          >
            <div>
              <div className="mb-5 w-14 h-14 flex items-center justify-center rounded-full" style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)' }}>
                {roles[0].icon}
              </div>
              <h3 className="text-2xl mb-3" style={{ fontFamily: '"Playfair Display", Georgia, serif', color: '#FAF7F2' }}>
                {roles[0].title}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: '#A89070', fontFamily: 'Roboto, sans-serif' }}>
                {roles[0].description}
              </p>
            </div>
            <div className="mt-8 flex items-center justify-between">
              <div>
                <p className="text-xs" style={{ color: '#C9A84C', fontFamily: 'Roboto, sans-serif' }}>{roles[0].commitment}</p>
                <p className="text-xs mt-1" style={{ color: '#6B5A3E', fontFamily: 'Roboto, sans-serif' }}>{roles[0].spots} seats available</p>
              </div>
              <button
                className="px-5 py-2 text-sm font-medium transition-all duration-200"
                style={{
                  background: 'linear-gradient(135deg, #C9A84C, #8B6914)',
                  color: '#1A0A00',
                  borderRadius: '6px',
                  fontFamily: 'Roboto, sans-serif',
                  letterSpacing: '0.05em',
                }}
              >
                Apply Now
              </button>
            </div>
          </div>

          {/* Event Curator */}
          <div
            className="md:col-span-7 relative p-7 flex flex-col justify-between cursor-pointer transition-all duration-300"
            style={{
              background: hoveredId === 'curator' ? 'rgba(139,26,26,0.15)' : 'rgba(139,26,26,0.07)',
              border: `1px solid ${hoveredId === 'curator' ? 'rgba(201,168,76,0.4)' : 'rgba(201,168,76,0.15)'}`,
              borderRadius: '16px',
              minHeight: '160px',
            }}
            onMouseEnter={() => setHoveredId('curator')}
            onMouseLeave={() => setHoveredId(null)}
          >
            <div className="flex items-start gap-5">
              <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-full" style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.25)' }}>
                {roles[1].icon}
              </div>
              <div className="flex-1">
                <h3 className="text-xl mb-2" style={{ fontFamily: '"Playfair Display", Georgia, serif', color: '#FAF7F2' }}>
                  {roles[1].title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: '#A89070', fontFamily: 'Roboto, sans-serif' }}>
                  {roles[1].description}
                </p>
              </div>
            </div>
            <div className="mt-5 flex items-center justify-between">
              <span className="text-xs" style={{ color: '#C9A84C', fontFamily: 'Roboto, sans-serif' }}>{roles[1].commitment} · {roles[1].spots} openings</span>
              <button className="text-xs px-4 py-1.5 transition-all duration-200" style={{ border: '1px solid rgba(201,168,76,0.4)', color: '#C9A84C', borderRadius: '5px', fontFamily: 'Roboto, sans-serif' }}>
                Learn More
              </button>
            </div>
          </div>

          {/* Cultural Ambassador */}
          <div
            className="md:col-span-4 relative p-7 flex flex-col justify-between cursor-pointer transition-all duration-300"
            style={{
              background: hoveredId === 'ambassador' ? 'rgba(26,42,74,0.4)' : 'rgba(26,42,74,0.25)',
              border: `1px solid ${hoveredId === 'ambassador' ? 'rgba(201,168,76,0.4)' : 'rgba(201,168,76,0.15)'}`,
              borderRadius: '16px',
              minHeight: '160px',
            }}
            onMouseEnter={() => setHoveredId('ambassador')}
            onMouseLeave={() => setHoveredId(null)}
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-11 h-11 flex items-center justify-center rounded-full" style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.25)' }}>
                {roles[2].icon}
              </div>
              <div>
                <h3 className="text-lg mb-2" style={{ fontFamily: '"Playfair Display", Georgia, serif', color: '#FAF7F2' }}>
                  {roles[2].title}
                </h3>
                <p className="text-xs leading-relaxed" style={{ color: '#A89070', fontFamily: 'Roboto, sans-serif' }}>
                  {roles[2].description}
                </p>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <span className="text-xs" style={{ color: '#C9A84C', fontFamily: 'Roboto, sans-serif' }}>{roles[2].commitment}</span>
              <button className="text-xs px-4 py-1.5 transition-all duration-200" style={{ border: '1px solid rgba(201,168,76,0.4)', color: '#C9A84C', borderRadius: '5px', fontFamily: 'Roboto, sans-serif' }}>
                Apply
              </button>
            </div>
          </div>

          {/* Heritage Volunteer */}
          <div
            className="md:col-span-3 relative p-7 flex flex-col justify-between cursor-pointer transition-all duration-300"
            style={{
              background: hoveredId === 'volunteer' ? 'rgba(26,58,42,0.4)' : 'rgba(26,58,42,0.25)',
              border: `1px solid ${hoveredId === 'volunteer' ? 'rgba(201,168,76,0.4)' : 'rgba(201,168,76,0.15)'}`,
              borderRadius: '16px',
              minHeight: '160px',
            }}
            onMouseEnter={() => setHoveredId('volunteer')}
            onMouseLeave={() => setHoveredId(null)}
          >
            <div>
              <div className="mb-4 w-11 h-11 flex items-center justify-center rounded-full" style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.25)' }}>
                {roles[3].icon}
              </div>
              <h3 className="text-lg mb-2" style={{ fontFamily: '"Playfair Display", Georgia, serif', color: '#FAF7F2' }}>
                {roles[3].title}
              </h3>
              <p className="text-xs leading-relaxed" style={{ color: '#A89070', fontFamily: 'Roboto, sans-serif' }}>
                {roles[3].description}
              </p>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <span className="text-xs" style={{ color: '#C9A84C', fontFamily: 'Roboto, sans-serif' }}>{roles[3].spots}+ spots</span>
              <button className="text-xs px-4 py-1.5 transition-all duration-200" style={{ border: '1px solid rgba(201,168,76,0.4)', color: '#C9A84C', borderRadius: '5px', fontFamily: 'Roboto, sans-serif' }}>
                Join
              </button>
            </div>
          </div>
        </div>

        {/* Bottom CTA strip */}
        <div
          className="mt-8 p-6 flex flex-col md:flex-row items-center justify-between gap-4"
          style={{
            background: 'rgba(201,168,76,0.08)',
            border: '1px solid rgba(201,168,76,0.2)',
            borderRadius: '12px',
          }}
        >
          <div>
            <p className="text-sm font-medium" style={{ fontFamily: '"Playfair Display", Georgia, serif', color: '#FAF7F2' }}>
              Not sure which role fits you?
            </p>
            <p className="text-xs mt-1" style={{ color: '#A89070', fontFamily: 'Roboto, sans-serif' }}>
              Our heritage team will guide you to the perfect opportunity.
            </p>
          </div>
          <button
            className="px-8 py-3 text-sm font-medium whitespace-nowrap transition-all duration-200 hover:opacity-90"
            style={{
              background: 'transparent',
              border: '1px solid #C9A84C',
              color: '#C9A84C',
              borderRadius: '6px',
              fontFamily: 'Roboto, sans-serif',
              letterSpacing: '0.08em',
            }}
          >
            Talk to Our Team
          </button>
        </div>
      </div>

      {/* Gold bottom border */}
      <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, #C9A84C, transparent)' }} />
    </section>
  );
}
